/// <amd-module name="Feed/Legacy/DataServices/FeedsDataService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import {
    Feed,
    FeedCapabilities,
    FeedPermission,
    FeedRetentionPolicy,
    FeedView,
    Package,
    PackageMetrics,
    PackageMetricsQuery,
    PackageVersion,
    PackageVersionMetrics,
    PackageVersionMetricsQuery
} from "Feed/Common/Contracts/Feed";
import { getFeedClient, IFeedRestClient } from "Feed/Common/RestClient/Feed";
import { Feed as CustomFeed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

import { getFullyQualifiedFeedId } from "../Helpers/FeedNameResolver";

export interface IFeedDataService extends IVssService {
    getFeedPermissionsAsync(
        feed: Feed,
        includeIds?: boolean,
        excludeInheritedPermissions?: boolean,
        identityDescriptors?: string,
        includeDeleted?: boolean
    ): Promise<FeedPermission[]>;
    setFeedPermissionsAsync(feed: Feed, feedPermissions: FeedPermission[]): Promise<FeedPermission[]>;
    getFeedViewPermissionsAsync(
        feed: Feed,
        viewId: string,
        includeIds?: boolean,
        excludeInheritedPermissions?: boolean,
        identityDescriptors?: string
    ): Promise<FeedPermission[]>;
    setFeedViewPermissionsAsync(
        feed: Feed,
        viewId: string,
        feedPermissions: FeedPermission[]
    ): Promise<FeedPermission[]>;
    getFeedViewsAsync(projectId: string, feedId: string): Promise<FeedView[]>;
    getFeedViewAsync(projectId: string, feedId: string, viewId: string): Promise<FeedView>;
    updateFeedAsync(feed: Feed): Promise<Feed>;
    createFeedAsync(projectId: string, feed: Feed): Promise<Feed>;
    getFeedAsync(projectId: string, feedId: string): Promise<Feed>;
    getDeletedFeedsAsync(projectId?: string): Promise<Feed[]>;
    deleteFeedAsync(projectId: string, feedId: string): Promise<void>;
    createFeedViewAsync(projectId: string, feedId: string, view: FeedView): Promise<FeedView>;
    updateFeedViewAsync(projectId: string, feedId: string, view: FeedView, viewId: string): Promise<FeedView>;
    deleteFeedViewAsync(projectId: string, feedId: string, viewId: string): Promise<void>;
    getFeedRetentionPolicy(projectId: string, feedId: string): Promise<FeedRetentionPolicy>;
    setFeedRetentionPolicyAsync(
        projectId: string,
        feedId: string,
        retentionPolicy: FeedRetentionPolicy
    ): Promise<FeedRetentionPolicy>;
    deleteFeedRetentionPolicy(projectId: string, feedId: string): Promise<FeedRetentionPolicy>;
    updateCapability(feedId: string, capabilities: FeedCapabilities): Promise<void>;
    /**
     * Should not be used directly other than PackageHubDataService which filters packages by enabled protocol
     */
    getPackagesAsync(
        feed: CustomFeed,
        protocolType: string,
        top: number,
        skip: number,
        includeDeleted: boolean,
        isListed: boolean,
        packageNameQuery?: string,
        includeDescription?: boolean
    ): Promise<Package[]>;
    getPackageAsync(feed: CustomFeed, packageId: string, includeDescription?: boolean): Promise<Package>;
    getPackageVersionAsync(
        feed: CustomFeed,
        packageId: string,
        versionId: string,
        isListed: boolean,
        isDeleted: boolean
    ): Promise<PackageVersion>;
    getPackageVersionsAsync(
        projectId: string,
        feedId: string,
        packageId: string,
        isListed: boolean,
        isDeleted: boolean
    ): Promise<PackageVersion[]>;
    queryPackageMetricsAsync(projectId: string, feedId: string, packageIds: string[]): Promise<PackageMetrics[]>;
    queryPackageVersionMetricsAsync(
        projectId: string,
        feedId: string,
        packageId: string,
        packageVersionIds: string[]
    ): Promise<PackageVersionMetrics[]>;
}

class FeedsDataService extends VssService implements IFeedDataService {
    private get FeedRestClient(): IFeedRestClient {
        return getFeedClient(this.pageContext);
    }

    public getFeedPermissionsAsync(
        feed: Feed,
        includeIds?: boolean,
        excludeInheritedPermissions?: boolean,
        identityDescriptors?: string,
        includeDeleted?: boolean
    ): Promise<FeedPermission[]> {
        const projectId = this.getProjectId(feed);
        const feedIdOrName = feed.deletedDate == null ? feed.id : feed.name;
        return this.FeedRestClient.getFeedPermissions(
            feedIdOrName,
            projectId,
            includeIds,
            excludeInheritedPermissions,
            identityDescriptors,
            includeDeleted
        );
    }

    public setFeedPermissionsAsync(feed: Feed, feedPermissions: FeedPermission[]): Promise<FeedPermission[]> {
        const projectId = this.getProjectId(feed);
        return this.FeedRestClient.setFeedPermissions(feedPermissions, feed.id, projectId);
    }

    public getFeedViewPermissionsAsync(
        feed: Feed,
        viewId: string,
        includeIds?: boolean,
        excludeInheritedPermissions?: boolean,
        identityDescriptors?: string
    ): Promise<FeedPermission[]> {
        const projectId = this.getProjectId(feed);
        return this.FeedRestClient.getFeedPermissions(
            feed.id + "@" + viewId,
            projectId,
            includeIds,
            excludeInheritedPermissions,
            identityDescriptors
        );
    }

    public async setFeedViewPermissionsAsync(
        feed: Feed,
        viewId: string,
        feedPermissions: FeedPermission[]
    ): Promise<FeedPermission[]> {
        const projectId = this.getProjectId(feed);
        return this.FeedRestClient.setFeedPermissions(feedPermissions, feed.id + "@" + viewId, projectId);
    }

    public async getFeedViewsAsync(projectId: string, feedId: string): Promise<FeedView[]> {
        return this.FeedRestClient.getFeedViews(feedId, projectId);
    }

    public getFeedViewAsync(projectId: string, feedId: string, viewId: string): Promise<FeedView> {
        return this.FeedRestClient.getFeedView(feedId, viewId, projectId);
    }

    public updateFeedAsync(feed: Feed): Promise<Feed> {
        return this.FeedRestClient.updateFeed(feed, feed.id, feed.project ? feed.project.id : null);
    }

    public createFeedAsync(projectId: string, feed: Feed): Promise<Feed> {
        return this.FeedRestClient.createFeed(feed, projectId);
    }

    public getFeedAsync(projectId: string, feedId: string): Promise<Feed> {
        return this.FeedRestClient.getFeed(feedId, projectId, true /* includeDeletedUpstreams */);
    }

    public getDeletedFeedsAsync(projectId?: string): Promise<Feed[]> {
        return this.FeedRestClient.getFeedsFromRecycleBin(projectId);
    }

    public async deleteFeedAsync(projectId: string, feedId: string): Promise<void> {
        return this.FeedRestClient.deleteFeed(feedId, projectId);
    }

    public async createFeedViewAsync(projectId: string, feedId: string, view: FeedView): Promise<FeedView> {
        return this.FeedRestClient.createFeedView(view, feedId, projectId);
    }

    public async updateFeedViewAsync(
        projectId: string,
        feedId: string,
        view: FeedView,
        viewId: string
    ): Promise<FeedView> {
        return this.FeedRestClient.updateFeedView(view, feedId, viewId, projectId);
    }

    public async deleteFeedViewAsync(projectId: string, feedId: string, viewId: string): Promise<void> {
        return this.FeedRestClient.deleteFeedView(feedId, viewId, projectId);
    }

    public async getFeedRetentionPolicy(projectId: string, feedId: string): Promise<FeedRetentionPolicy> {
        return this.FeedRestClient.getFeedRetentionPolicies(feedId, projectId);
    }

    public async setFeedRetentionPolicyAsync(
        projectId: string,
        feedId: string,
        retentionPolicy: FeedRetentionPolicy
    ): Promise<FeedRetentionPolicy> {
        return this.FeedRestClient.setFeedRetentionPolicies(retentionPolicy, feedId, projectId);
    }

    public async deleteFeedRetentionPolicy(projectId: string, feedId: string): Promise<FeedRetentionPolicy> {
        await this.FeedRestClient.deleteFeedRetentionPolicies(feedId, projectId);
        return null;
    }

    public async updateCapability(feedId: string, capabilities: FeedCapabilities): Promise<void> {
        return this.FeedRestClient.updateCapability(
            capabilities,
            feedId,
            null /*projectId: null because all V1 feeds are collection scoped feeds */
        );
    }

    /**
     * Should not be used directly other than PackageHubDataService which filters packages by enabled protocol
     */
    public getPackagesAsync(
        feed: CustomFeed,
        protocolType: string,
        top: number,
        skip: number,
        includeDeleted: boolean,
        isListed: boolean,
        packageNameQuery: string = null,
        includeDescription: boolean = false
    ): Promise<Package[]> {
        if (packageNameQuery === "") {
            packageNameQuery = null;
        }

        const feedId = getFullyQualifiedFeedId(feed);
        const projectId = this.getProjectId(feed);
        const directUpstreamSourceId: string = feed.upstreamSource ? feed.upstreamSource.id : null;
        return this.FeedRestClient.getPackages(
            feedId,
            projectId,
            protocolType, // ProtocolType
            packageNameQuery,
            null, // NormalizedPackageName
            null, // IncludeUrls,
            null, // IncludeAllVersions
            isListed,
            null, // GetTopPackageVersions
            null, // IsRelease
            includeDescription,
            top,
            skip,
            includeDeleted,
            null, // isCached
            directUpstreamSourceId
        );
    }

    public getPackageAsync(feed: CustomFeed, packageId: string, includeDescription?: boolean): Promise<Package> {
        const feedId = getFullyQualifiedFeedId(feed);
        const projectId = this.getProjectId(feed);
        return this.FeedRestClient.getPackage(
            feedId,
            packageId,
            projectId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            includeDescription
        );
    }

    public getPackageVersionAsync(
        feed: CustomFeed,
        packageId: string,
        versionId: string,
        isListed: boolean,
        isDeleted: boolean
    ): Promise<PackageVersion> {
        const includeUrls = null;
        const feedId = getFullyQualifiedFeedId(feed);
        const projectId = this.getProjectId(feed);
        return this.FeedRestClient.getPackageVersion(
            feedId,
            packageId,
            versionId,
            projectId,
            includeUrls,
            isListed,
            isDeleted
        );
    }

    public getPackageVersionsAsync(
        projectId: string,
        feedId: string,
        packageId: string,
        isListed: boolean,
        isDeleted: boolean
    ): Promise<PackageVersion[]> {
        const includeUrls = null;
        return this.FeedRestClient.getPackageVersions(feedId, packageId, projectId, includeUrls, isListed, isDeleted);
    }

    public queryPackageMetricsAsync(
        projectId: string,
        feedId: string,
        packageIds: string[]
    ): Promise<PackageMetrics[]> {
        const query: PackageMetricsQuery = { packageIds: packageIds };
        return this.FeedRestClient.queryPackageMetrics(query, feedId, projectId);
    }

    public queryPackageVersionMetricsAsync(
        projectId: string,
        feedId: string,
        packageId: string,
        packageVersionIds: string[]
    ): Promise<PackageVersionMetrics[]> {
        const query: PackageVersionMetricsQuery = { packageVersionIds: packageVersionIds };
        return this.FeedRestClient.queryPackageVersionMetrics(query, feedId, packageId, projectId);
    }

    private getProjectId(feed: Feed) {
        return feed.project ? feed.project.id : null;
    }
}

Services.add("IFeedDataService", { serviceFactory: FeedsDataService });
